/* eslint no-console: 0 */
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useHistory } from 'react-router-dom';

import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';
import { useGetCurrentUser } from '../../../hooks/useGetCurrentUser.ts';
import Tile from '../../bits/Tile.jsx';
import TileActionRowLink from '../../bits/TileActionRowLink.jsx';
import Loading from '../Loading.jsx';
import { PhoneVerificationStatusIcon } from './PhoneVerificationStatusIcon.tsx';

interface SettingsHomeProps { }

interface SettingsLocationState {
  phoneVerificationSucceeded?: boolean;
}

export const SettingsHome: React.FC<SettingsHomeProps> = () => {
  const { data: user, isFetching } = useGetCurrentUser();
  const history = useHistory<SettingsLocationState>();
  const phoneVerificationSucceededInHistory = (
    history.location.state?.phoneVerificationSucceeded === true
  );
  const [phoneVerificationSucceeded, setPhoneVerificationSucceeded] = useState(
    phoneVerificationSucceededInHistory,
  );

  useSetModalTitle('Settings');

  useEffect(() => {
    if (!phoneVerificationSucceededInHistory) return;

    setPhoneVerificationSucceeded(true);
    history.replace({
      ...history.location,
      state: {
        ...history.location.state,
        phoneVerificationSucceeded: false,
      },
    });
  }, [history, phoneVerificationSucceededInHistory]);

  const dismissPhoneVerificationSuccess = useCallback(() => {
    setPhoneVerificationSucceeded(false);
  }, []);

  const fullName = useMemo(() => {
    const { firstName, lastName } = user || {};

    if (!firstName && !lastName) {
      return 'Unknown';
    }

    return `${firstName || ''} ${lastName || ''}`.trim();
  }, [user]);

  // Show loading indicator if we're fetching content AND have nothing to display. This means
  // refreshes will not show loading. Important because of the query invalidation that can happen
  // (we want query invalidation to show loading)
  const isLoading = isFetching && !user;

  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && <div>
        <Tile>
          <TileActionRowLink
            label="Name"
            description={fullName}
            to={{
              pathname: 'settings/edit-name',
              state: { origin: 'settings_list' },
            }}
          />
          <TileActionRowLink
            label="Phone Number"
            description={user?.phone
              ? (
                <span className="phone-number-setting-description">
                  <span>{user.phone.numberFormatted}</span>
                  <PhoneVerificationStatusIcon
                    verified={user.phone.verified}
                    size="small"
                  />
                </span>
              )
              : 'N/A'}
            to={{
              pathname: 'settings/edit-phone',
              state: { origin: 'settings_list' },
            }}
          />
        </Tile>

        <Tile>
          <TileActionRowLink
            label="Change Password"
            to={{
              pathname: 'settings/change-password',
              state: { origin: 'settings_list' },
            }}
          />
        </Tile>
        <Tile>
          <TileActionRowLink
            label="Notifications"
            to={{
              pathname: 'settings/notifications',
              state: { origin: 'settings_list' },
            }}
          />
        </Tile>

        {user?.apiAccessEnabled &&
          <Tile>
            <TileActionRowLink
              label="API Access"
              to={{
                pathname: 'settings/api',
                state: { origin: 'settings_list' },
              }}
            />
          </Tile>
        }
      </div>}

      <Snackbar
        open={phoneVerificationSucceeded}
        autoHideDuration={6000}
        onClose={dismissPhoneVerificationSuccess}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity="success"
          onClose={dismissPhoneVerificationSuccess}
        >
          Phone number verified successfully.
        </MuiAlert>
      </Snackbar>
    </>
  );
};
